import { IPublicClientApplication } from '@azure/msal-browser'
import { apiScope } from '../../configuration/config'
import { ILoginRequest } from '../../core/authentication/types'
import { showSessionTimeoutAlert } from '../utils/session-alert'

let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}> = []

const processQueue = (error: Error | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  })

  failedQueue = []
}

/**
 * Refreshes the authentication token using MSAL
 */
export const refreshAuthToken = async (
  msalInstance: IPublicClientApplication
): Promise<boolean> => {
  const accounts = msalInstance.getAllAccounts()

  if (accounts.length === 0) {
    console.error('No accounts found for token refresh')
    return false
  }

  const loginRequest: ILoginRequest = {
    scopes: ['User.Read', 'User.Read.All', apiScope],
  }

  const request = {
    ...loginRequest,
    account: accounts[0],
  }

  try {
    const response = await msalInstance.acquireTokenSilent(request)

    const { encryptAndStoreToken } = await import(
      '../../core/authentication/request-token/request-token'
    )

    await encryptAndStoreToken(response.idToken)
    sessionStorage.setItem('lastTokenRefresh', Date.now().toString())

    return true
  } catch (error) {
    console.error('Silent token refresh failed:', error)
    return false
  }
}

/**
 * Interceptor for fetch requests that handles token expiration
 */
export const createFetchInterceptor = (
  msalInstance: IPublicClientApplication
) => {
  const originalFetch = window.fetch

  window.fetch = async (...args) => {
    const [resource, config] = args

    try {
      let response = await originalFetch(resource, config)

      // Ensure response is valid before checking status
      if (!response) {
        throw new Error('Fetch returned undefined response')
      }

      const isOurApi =
        typeof resource === 'string' &&
        !resource.includes('login.microsoftonline.com') &&
        !resource.includes('login.windows.net') &&
        !resource.includes('appConfiguration.json')

      if (response.status === 401 && isOurApi) {
        const hasIdToken = !!sessionStorage.getItem('idToken')
        const hasEncryptedToken = !!sessionStorage.getItem('encryptedToken')
        const hasValidSession = hasIdToken && hasEncryptedToken

        // If we have a valid session but still got 401, it's a permission issue - don't refresh
        if (hasValidSession) {
          return response
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then(() => {
              return originalFetch(resource, config)
            })
            .catch((err) => {
              return Promise.reject(err)
            })
        }

        isRefreshing = true

        // Show loading alert
        const hideAlert = showSessionTimeoutAlert()

        try {
          const refreshSuccessful = await refreshAuthToken(msalInstance)

          if (refreshSuccessful) {
            if (config?.headers) {
              const encryptedToken =
                sessionStorage.getItem('encryptedToken') || ''
              const encryptedAESKey =
                sessionStorage.getItem('encryptedAESKey') || ''

              const newConfig = {
                ...config,
                headers: {
                  ...config.headers,
                  EncryptedToken: encryptedToken,
                  EncryptedAESKey: encryptedAESKey,
                },
              }

              response = await originalFetch(resource, newConfig)
              processQueue(null)
            }
            // Hide alert on success
            hideAlert()
          } else {
            hideAlert()
            processQueue(new Error('Token refresh failed'))
            sessionStorage.clear()
            window.location.href = '/'
          }
        } catch (error) {
          hideAlert()
          processQueue(
            error instanceof Error ? error : new Error('Token refresh failed')
          )
          sessionStorage.clear()
          window.location.href = '/'
          throw error
        } finally {
          isRefreshing = false
        }
      }

      return response
    } catch (fetchError) {
      console.error('Fetch error:', fetchError)
      throw fetchError
    }
  }
}
